// Request plugin.
import * as actions from './interface/actions'
import * as eventTypes from './interface/eventTypes'
import * as authorizations from './authorization'
import { handleRequest } from './sagas'

// Other plugins.
import { getRequestInfo, getPlatform } from '../auth/interface/selectors'

// Helpers.
import { getVersion } from '../common/version'
import { mergeValues } from '../common/utils'

// Libraries.
import { omit } from 'lodash/fp'

/**
 * Request helper factory function.
 * @method createRequestHelper
 * @param  {Object} container The bottle container.
 * @return {Function} The request function.
 */
export default function createRequestHelper (container) {
  const { emitEvent, context } = container

  return async function doRequest (options) {
    const requestInfo = getRequestInfo(context.getState())
    const platform = getPlatform(context.getState())

    // Merge the default request options from state into the provided options.
    //    The provided options take priority.
    // Workaround: The options for link and cpaas have different names
    // Look for options (cpaas) first, then requestOptions (link)
    const defaultOptions = requestInfo.options || requestInfo.requestOptions
    options = mergeValues(defaultOptions, options)

    // Create an action to both pass in and to get a requestId.
    const requestAction = actions.request(options)

    // Workaround: For backwards-compatibility, we need to log the request action
    //    for the LogHandler, but we don't want to actually dispatch the action
    //    (otherwise it'll be processed...). So we manually log the fake action
    //    to imitate how the logger would log an action.
    logFakeAction(context, requestAction)

    // handleRequest is a generator (so that it can be used as a saga by
    //    redux-saga). So we need the "setup" the generator and then invoke it.
    const gen = handleRequest(platform, requestAction)
    const result = await gen.next().value

    // If the platform was specified and this is not a 3.X build, perform the
    //    authorization check side-effect.
    if (platform && !getVersion().startsWith('3')) {
      // Call the 'authorization' function specific for this platform.
      //    They're all named ${platform}Authorization so it's easier to call them.
      const error = authorizations[`${platform}Authorization`](result)

      if (error) {
        emitEvent(eventTypes.REQUEST_ERROR, { error })
      }
    }

    const responseAction = actions.response(requestAction.meta.requestId, result, !!result.error)
    logFakeAction(context, responseAction)

    return result
  }
}

/**
 * Workaround for logging an action without dispatching it.
 *
 * The Log plugin will automatically log any action that is dispatched, but in
 *    some cases we don't want to dispatch an action anymore. For backwards-
 *    compatibility, the action still needs to be logged. This function logs an
 *    action as if it were dispatched.
 *
 * Caveat: The action shouldn't update state (since then it _should_ be dispatched),
 *    since the prev/next state logs would then be incorrect.
 */
export function logFakeAction ({ getState, container }, action) {
  const log = container.logManager.getLogger('ACTION')
  const logState = getState().config.logs
  const { logActions, logLevel } = logState

  // If logActions are falsy, don't log the action.
  if (!logActions) {
    return
  }

  /*
   * Action log options:
   *  - collapsed: Whether to use group or groupCollapsed.
   *  - actionOnly: Whether to log the 'prev state' and 'next state'.
   *  - exposePayloads: Whether to log the payloads or not.
   *  - logLevel: Whether to log any action/state details.
   */
  const { collapsed, actionOnly, exposePayloads } = logActions

  const groupMethod = collapsed ? 'groupCollapsed' : 'group'
  const hidePayload = exposePayloads === false || (action.meta && action.meta.isSensitive)
  const actionToLog = hidePayload ? omit('payload', action) : action

  log[groupMethod](`action ${action.type} @ ${getTime()} (in 0.00 ms)`)
  if (logLevel === 'DEBUG') {
    if (actionOnly) {
      log.debug('action    ', actionToLog)
    } else {
      log.debug('prev state', getState())
      log.debug('action    ', actionToLog)
      // Assume the action does not update state, so use the same state value.
      log.debug('next state', getState())
    }
  }
  log.groupEnd()
}

/*
 * Helper function.
 * Get a timestamp formatted in the specific way that redux-logger uses for actions.
 * Code copy/pasted from redux-logger: https://github.com/LogRocket/redux-logger/blob/master/src/helpers.js
 */
function getTime () {
  const time = new Date()
  const repeat = (str, times) => new Array(times + 1).join(str)
  const pad = (num, maxLength) => repeat('0', maxLength - num.toString().length) + num
  return `${pad(time.getHours(), 2)}:${pad(time.getMinutes(), 2)}:${pad(time.getSeconds(), 2)}.${pad(
    time.getMilliseconds(),
    3
  )}`
}
